// src/DigitalMarketing_In_Noida.tsx

import React, { useState } from "react"; // Import useState
import { motion } from "framer-motion";

import { FaLaptopCode, FaCertificate, FaBriefcase, FaFileAlt, FaComments, FaUsers } from 'react-icons/fa';
import { Helmet } from "react-helmet-async";

// Placeholder import for Digital Marketing main image
import Img2 from "../../assets/Internship/marketing.png"; // Using a generic image, ideally a Digital Marketing specific one

import JoinNowFormPopup from "../../componants/JoinNowFormPopup";


// Placeholder imports for logos - these assume images are in your public folder
// If you prefer to import them directly from an assets folder, adjust paths accordingly.

// Digital Marketing tools/platforms logos (placeholders - replace with actual paths if available)
import googleads from "../../assets/Internship/googleads.jpg"; // Example: path to Google Ads logo
import semrush from "../../assets/Internship/semrush.png"; // Example: path to SEMrush logo
import hubspot from "../../assets/Internship/hubspot.webp";     // Example: path to HubSpot logo
import mailchimp from "../../assets/Internship/mailchimp.jpg";   // Example: path to Mailchimp logo
import googleanalytics from "../../assets/Internship/Data_Analytics_logo.jpg"; // Example: path to Google Analytics logo
import hootsuite from "../../assets/Internship/hootsuite.jpg"; // Example: path to Hootsuite logo


import img from "../../assets/Internship/our-process.webp";

// Animation variants
const fadeInLeft = {
  hidden: { opacity: 0, x: -80 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 80 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
};



const DigitalMarketingInNoida: React.FC = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false); // State to control popup visibility
  const [selectedCourseTitle, setSelectedCourseTitle] = useState(""); // State to store the course title

  const handleOpenPopup = (courseTitle: string) => { // Function to open the popup
    setSelectedCourseTitle(courseTitle); // Set the selected course title
    setIsPopupOpen(true); // Open the popup
  };

  const handleClosePopup = () => { // Function to close the popup
    setIsPopupOpen(false); // Close the popup
    setSelectedCourseTitle(""); // Clear selected course when closing
  };


  const whyChooseUsBenefits = [
    {
      icon: <FaLaptopCode className="text-blue-400 text-5xl mb-4" />,
      title: "Hybrid Mode Class",
      description: "Learn flexibly with combined online and offline classroom training sessions.",
    },
    {
      icon: <FaCertificate className="text-blue-400 text-5xl mb-4" />,
      title: "Internship Certificate",
      description: "Receive an internship certificate for hands-on industry project experience.",
    },
    {
      icon: <FaBriefcase className="text-blue-400 text-5xl mb-4" />,
      title: "100% Placement Assistance",
      description: "Complete placement support provided until you secure a suitable job.",
    },
    {
      icon: <FaFileAlt className="text-blue-400 text-5xl mb-4" />,
      title: "CV Building & ATS Score",
      description: "Optimize your resume for ATS and improve shortlisting chances significantly.",
    },
    {
      icon: <FaComments className="text-blue-400 text-5xl mb-4" />,
      title: "Mock Interviews",
      description: "Practice with mock interviews to boost real interview performance confidence.",
    },
    {
      icon: <FaUsers className="text-blue-400 text-5xl mb-4" />,
      title: "15 Students Per Batch",
      description: "Small batches ensure personalized attention and better student-teacher interaction.",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-100 font-sans antialiased">
      <Helmet>
        <title>Digital Marketing Certification in Noida | Appwars Technologies</title>
        <meta name="description" content="Master Digital Marketing with comprehensive certification in Noida. Learn SEO, SEM, Social Media Marketing, Content Marketing, and Analytics. 100% placement, hybrid classes, and internship certificates by Appwars Technologies." />
        <meta name="keywords" content="digital marketing, SEO, SEM, social media marketing, content marketing, analytics, Google Ads, SEMrush, HubSpot, Mailchimp, Google Analytics, Hootsuite, Noida, training, placement, internship, Appwars Technologies, Nadezhda Ventures" />
        <meta name="author" content="Appwars Technologies / Nadezhda Ventures" />
        <meta property="og:title" content="Digital Marketing Certification in Noida | Appwars Technologies" />
        <meta property="og:description" content="Boost your career with comprehensive Digital Marketing certification programs in Noida." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="http://yourwebsite.com/digital-marketing-noida" /> {/* Replace with your actual website URL */}
        <link rel="canonical" href="http://yourwebsite.com/digital-marketing-noida" /> {/* Replace with your actual canonical URL */}
      </Helmet>



      <main>
       {/* SECTION 1 - Hero Section with Transparent Image and Text Overlay */}
            <section className="relative overflow-hidden flex items-center justify-center h-[400px] md:h-[400px] lg:h-[400px]">
              {/* Transparent Background Image */}
              <div className="absolute inset-0 z-0">
                <img
                  src="https://png.pngtree.com/thumb_back/fh260/background/20190223/ourmid/pngtree-blue-fluttering-technology-background-boardbackground-image_83398.jpg" // Using a generic technology background image
                  alt="Digital Marketing Transparent Background"
                  className="w-full h-full object-cover opacity-80" // Adjust opacity for desired transparency
                />
                {/* Optional: Add a subtle overlay for better text readability and darker background */}
                <div className="absolute inset-0 bg-black opacity-70"></div> {/* Adjust opacity as needed */}
              </div>

              {/* Text and Image Content */}
              <div className="min-h-screen flex flex-col md:flex-row items-center justify-center gap-12 px-6 py-20 max-w-7xl mx-auto relative z-10">
                <motion.div
                  className="flex-1 text-center md:text-left"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInLeft}
                >
                  <h1 className="text-4xl md:text-5xl font-extrabold text-orange-500 mb-6">
                    Digital Marketing Certification
                  </h1>
                  <p className="text-gray-300 text-lg leading-relaxed mb-6">
                    Become a digital marketing expert with Nadezhda Venture's comprehensive certification program. Master strategies to drive online visibility, engagement, and conversions.
                  </p>

                  <button
                    className="bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded-lg font-semibold transition text-white"
                    onClick={() => handleOpenPopup("Digital Marketing Certification")} // Pass the course title
                  >
                    Join Now !
                  </button>
                </motion.div>

                <motion.div
                  className="flex-1 w-full max-w-2xl flex justify-center items-center"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInRight}
                >
                  <img
                    src={Img2}
                    alt="Digital Marketing Showcase"
                    className="rounded-xl shadow-lg w-full h-auto object-contain max-h-[400px]"
                  />
                </motion.div>
              </div>
            </section>


       {/* Digital Marketing Tools Covered Section */}
<section className="px-6 md:px-20 py-24 bg-gray-300 text-orange-600">
  <h2 className="text-5xl font-bold text-center mb-10">Key Digital Marketing Tools</h2>
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6">
    {/* Google Ads */}
    <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
      <img src={googleads} alt="Google Ads Icon" className="w-16 h-16 mb-2"/>
      <h3 className="text-lg font-semibold text-center">Google Ads</h3>
      <button
        onClick={() => handleOpenPopup("Google Ads")} // Pass the course title
        className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
    </div>

    {/* SEMrush */}
    <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
      <img src={semrush} alt="SEMrush Icon" className="w-16 h-16 mb-2"/>
      <h3 className="text-lg font-semibold text-center">SEMrush</h3>
      <button
        onClick={() => handleOpenPopup("SEMrush")} // Pass the course title
        className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
    </div>

    {/* HubSpot */}
    <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
      <img src={hubspot} alt="HubSpot Icon" className="w-18 h-16 mb-2"/>
      <h3 className="text-lg font-semibold text-center">HubSpot</h3>
      <button
        onClick={() => handleOpenPopup("HubSpot")} // Pass the course title
        className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
    </div>

    {/* Mailchimp */}
    <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
      <img src={mailchimp} alt="Mailchimp Icon" className="w-16 h-16 mb-2"/>
      <h3 className="text-lg font-semibold text-center">Mailchimp</h3>
      <button
        onClick={() => handleOpenPopup("Mailchimp")} // Pass the course title
        className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
    </div>

    {/* Google Analytics */}
    <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
      <img src={googleanalytics} alt="Google Analytics Icon" className="w-18 h-16 mb-2"/>
      <h3 className="text-lg font-semibold text-center">Google Analytics</h3>
      <button
        onClick={() => handleOpenPopup("Google Analytics")} // Pass the course title
        className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
    </div>

    {/* Hootsuite */}
    <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
      <img src={hootsuite} alt="Hootsuite Icon" className="w-16 h-16 mb-2"/>
      <h3 className="text-lg font-semibold text-center">Hootsuite</h3>
      <button
        onClick={() => handleOpenPopup("Hootsuite")} // Pass the course title
        className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
    </div>

   </div>

</section>

        {/* Why Choose Us Section (from image_ab7e7d.png, styling adapted) */}
        <section className="px-6 md:px-20 py-16 md:py-24 bg-white text-gray-800">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">Why Choose Us?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {whyChooseUsBenefits.map((benefit, index) => (
              <div
                key={index}
                className="flex flex-col items-center text-center p-6 bg-gray-800 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                {benefit.icon}
                <h3 className="text-xl font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-gray-300 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </section>


       {/* Our Interview Process Section */}
  <h2 className="text-3xl md:text-4xl font-bold text-center mb-10 text-orange-500">Our Interview Process</h2>

    <img
      src={img} // Path to your process image file
      alt="Our Interview Process"
      className="w-full h-auto object-cover"
    />

      </main>

     

      {/* Conditionally render the popup */}
      {isPopupOpen && (
        <JoinNowFormPopup
          onClose={handleClosePopup}
          courseTitle={selectedCourseTitle}
        />
      )}
    </div>
  );
};

export default DigitalMarketingInNoida;